<template>
  <div class="invoice-collections-container">
    <!-- New Month Button -->
    <div class="button-section">
      <CustomButton
        text="Notificación pago vencido"
        variant="transparent"
        :show-icon="true"
        icon-alt=""
        :icon-src="voidIcon"
        @click="openSendingNotificationModal"
      />
      <CustomButton
        text="+ Nuevo mes de Facturación"
        variant="secondary"
        :show-icon="false"
        icon-alt="Cargar documentos"
        @click="openNewMonthModal"
      />
    </div>

    <!-- History Section -->
    <InvoiceCollectionsHistory
      :history-data="historyData"
      :is-loading="isLoadingHistory"
      :show-edit-button="true"
      :show-concept-column="false"
      :show-payment-date-column="true"
      :show-receipt-column="true"
      :receipt-column-title="'COMPROBANTE'"
      :show-receipt-info-button="false"
      :show-amount-to-pay-column="false"
      :show-prorroga-column="true"
      @download-document="downloadDocument"
      @edit-document="editDocument"
      @reload-data="loadHistoryData"
    />

    <!-- New Document Modal -->
    <BillingMonthModal
      :is-visible="showModal"
      :is-new-register="isNewRegister"
      :document-type="editingDocumentType"
      :billing-month="editingBillingMonth"
      :billing-year="editingBillingYear"
      :current-payment-due-date="editingPaymentDueDate"
      :current-file-name="editingFileName"
      :is-loading="isLoading"
      :current-payment-made-date="editingPaymentMadeDate"
      @close="closeModal"
      @save="handleModalSave"
      :origen="'invoice-collections'"
    />

    <SendingDuePaymentNotificationModal
      :is-visible="showSendingNotificationModal"
      :business-id="bussinessId"
      :selected-period="selectedPeriod"
      :selected-notification-type="selectedNotificationType"
      @close="closeSendingNotificationModal"
    />

    <!-- Toast Message -->
    <ToastMessage
      v-model="showToast"
      :message="toastMessage"
      :submessage="toastSubmessage"
      :duration="10000"
      :type="toastType"
      :icon="toastIcon"
    />
    <CustomLoading :is-visible="isLoading" text="Procesando..." />
  </div>
</template>

<script setup lang="ts">
import { ref, inject, onMounted } from 'vue'
import CustomButton from './CustomButton.vue'
import { parseBillingMonth, months, formatMonthYear } from '@/utils/dateParsing'
import InvoiceCollectionsHistory from './InvoiceCollectionsHistory.vue'
import BillingMonthModal from './BillingMonthModal.vue'
import ToastMessage from './ToastMessage.vue'
import errorIcon from '@/assets/error-icon-white.svg'
import checkIcon from '@/assets/check-icon.svg'
import { ApiService } from '@/services/apiService'
import { useBulkLoadStore } from '@/stores/bulkLoad'
import CustomLoading from './CustomLoading.vue'
import { FileTypes, StatusAccountPeriodDto } from 'commons'
import type { HistoryItem } from '@/utils/file-history'
import { uploadFileToS3AndConfirm } from '@/utils/fileUpload'
import { normalizeAmount } from '../utils/amountUtils'
import SendingDuePaymentNotificationModal from './SendingDuePaymentNotificationModal.vue'
import voidIcon from '@/assets/void-icon.svg'

// API response interface
interface StatusAccountApiItem {
  id?: string
  month: number
  year: number
  paymentLimitDate?: Date | string
  paymentExtensionDate?: Date | string
  paymentMadeDate?: Date | string
  amountToPay?: number
  concept?: string
  invoiceNumber?: string
  statusAccountFiles: Array<{
    id?: string
    fileName?: string
    fileType: string
  }>
}

// Inject apiService
const apiService = inject<ApiService>('apiService')

if (!apiService) {
  throw new Error('apiService is not provided')
}

// Modal state
const showModal = ref(false)
const isNewRegister = ref(true)
const editingDocumentType = ref<'account-statement' | 'pre-invoice'>('account-statement')
const editingBillingMonth = ref('')
const editingBillingYear = ref('')
const editingPaymentDueDate = ref('')
const editingFileName = ref('')
const editingPaymentMadeDate = ref('')

// Toast message state
const showToast = ref(false)
const toastMessage = ref('')
const toastSubmessage = ref('')
const toastType = ref<'success' | 'error'>('success')
const toastIcon = ref<string | undefined>(undefined)
const isLoading = ref(false)

// History data
const historyData = ref<HistoryItem[]>([])

// Loading state for history data
const isLoadingHistory = ref(false)

const bussinessId = useBulkLoadStore().selectedGroup.id

const showSendingNotificationModal = ref(false)

const selectedPeriod = ref<string | undefined>()
const selectedNotificationType = ref<string | undefined>()

// Mock data for historyData while API is not working
const loadHistoryData = async () => {
  try {
    isLoadingHistory.value = true

    const groupId = useBulkLoadStore().selectedGroup.id
    const response = await apiService.getStatusAccountFiles(groupId)

    historyData.value = response.map(transformStatusAccountItem)
  } catch (error) {
    console.error('Hubo un error: Error loading history data ', error)
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al cargar el historial de documentos'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    isLoadingHistory.value = false
  }
}

function transformStatusAccountItem(item: StatusAccountApiItem): HistoryItem {
  const files = getFilesByType(item.statusAccountFiles)
  const formattedDates = getFormattedDates(item)

  return {
    id: item.id || '',
    billingMonth: formatMonthYear(item.month, item.year.toString()).toUpperCase(),
    ...formattedDates,
    accountStatement: files.accountStatement || '',
    preInvoice: files.preInvoice || '',
    invoice: files.invoice || '',
    accountStatementId: files.accountStatementId || '',
    preInvoiceId: files.preInvoiceId || '',
    invoiceId: files.invoiceId || '',
    receipt: files.receipt || '',
    receiptId: files.receiptId || '',
    amountToPay: normalizeAmount(item.amountToPay?.toString()),
    concept: item.concept || '',
    invoiceNumber: item.invoiceNumber || '',
    xmlInvoice: files.xmlInvoice || '',
    xmlInvoiceId: files.xmlInvoiceId || '',
  }
}

function getFormattedDates(item: StatusAccountApiItem) {
  return {
    paymentLimitDate: formatDate(item.paymentLimitDate),
    paymentExtensionDate: formatDate(item.paymentExtensionDate),
    paymentMadeDate: formatDate(item.paymentMadeDate),
  }
}

function getFilesByType(
  statusAccountFiles: Array<{ id?: string; fileName?: string; fileType: string }> = [],
) {
  const typesMap = {
    accountStatement: FileTypes.ACCOUNT_STATEMENT,
    preInvoice: FileTypes.PRE_INVOICE,
    invoice: FileTypes.INVOICE,
    receipt: FileTypes.PAYMENT_PROOF,
    xmlInvoice: FileTypes.XML_INVOICE,
  }

  const result: Record<string, string> = {}

  Object.entries(typesMap).forEach(([key, type]) => {
    const file = statusAccountFiles.find((f) => f.fileType === type)
    result[key] = file?.fileName || ''
    result[`${key}Id`] = file?.id || ''
  })

  return result
}

function formatDate(date?: Date | string): string {
  if (!date) return ''

  // If the API returned a string, try to parse it
  const parsedDate = typeof date === 'string' ? new Date(date) : date

  // Guard against invalid dates
  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) return ''

  // Use UTC to avoid timezone offset issues
  return parsedDate.toLocaleDateString('es-ES', { timeZone: 'UTC' })
}

// Modal methods
const openNewMonthModal = () => {
  isNewRegister.value = true
  editingDocumentType.value = 'account-statement'
  editingBillingMonth.value = ''
  editingBillingYear.value = ''
  editingPaymentDueDate.value = ''
  editingFileName.value = ''
  showModal.value = true
  editingPaymentMadeDate.value = ''
}

const closeModal = () => {
  showModal.value = false
}

const openSendingNotificationModal = () => {
  showSendingNotificationModal.value = true
  selectedPeriod.value = undefined
  selectedNotificationType.value = undefined
}

const closeSendingNotificationModal = () => {
  showSendingNotificationModal.value = false
}

// Convert payment due date from dd/mm/yyyy to ISO 8601 format
const convertToISO8601 = (dateString: string): string => {
  const [day, month, year] = dateString.split('/')
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
}

const handleNewRegister = async (data: {
  paymentDueDate: string
  files: { file: File; fileType: FileTypes }[]
  billingMonth?: string
  invoiceNumber?: string
  amountToPay?: string
  concept?: string
  paymentMadeDate?: string
  sendByEmail?: boolean
  idEmpresa?: string | number | null
  idEmpresaFromGetGroups?: boolean
}) => {
  console.log('Creating new billing month...')

  // Parse billing month to extract month and year
  const { month, year } = parseBillingMonth(data.billingMonth || '')

  // Create statusAccountFiles array with file information
  const statusAccountFiles = (data.files || []).map(
    (fileData: { file: File; fileType: FileTypes }) => ({
      id: '',
      fileName: fileData.file.name,
      fileType: fileData.fileType,
      contentType: fileData.file.type,
      loadState: '',
    }),
  )

  // Create StatusAccountPeriodDto for API call
  const statusAccountPeriodDto = new StatusAccountPeriodDto()
  statusAccountPeriodDto.groupId = useBulkLoadStore().selectedGroup.id
  statusAccountPeriodDto.month = month
  statusAccountPeriodDto.year = parseInt(year)
  // Concatenate idEmpresa with invoiceNumber for concept
  const invoiceNumber = data.invoiceNumber || ''
  const empresaId = data.idEmpresa ? String(data.idEmpresa) : ''
  statusAccountPeriodDto.concept =
    empresaId && invoiceNumber ? `${empresaId}${invoiceNumber}` : data.concept || ''
  statusAccountPeriodDto.invoiceNumber = invoiceNumber
  // paymentExtensionDate is calculated in the backend, so we don't send it
  statusAccountPeriodDto.invoiceDate = new Date()
  statusAccountPeriodDto.statusAccountFiles = statusAccountFiles
  statusAccountPeriodDto.amountToPay = parseFloat(data.amountToPay || '0')
  statusAccountPeriodDto.paymentMadeDate = data.paymentMadeDate
    ? new Date(convertToISO8601(data.paymentMadeDate))
    : undefined

  // Upload files if provided
  if (data.files && data.files.length > 0) {
    const response = await apiService.getUploadPresignedUrl(statusAccountPeriodDto)
    console.log('Upload response:', response)

    // Upload each file to S3 using the presigned URLs
    for (let i = 0; i < data.files.length; i++) {
      const fileData = data.files[i]
      const uploadInfo = response[i]

      if (uploadInfo) {
        await uploadFileToS3AndConfirm(
          fileData.file,
          uploadInfo.url,
          uploadInfo.statusAccountId,
          apiService,
        )
        console.log(`File ${fileData.file.name} uploaded successfully`)
      }
    }
  }

  // Send email if checkbox is checked
  if (data.sendByEmail) {
    try {
      const monthName = months[month - 1]
      await apiService.sendBillingAccessEmail(
        useBulkLoadStore().selectedGroup.id,
        monthName,
        year,
        data.paymentDueDate || '(No ingresada)',
        false,
      )
      console.log('Email de acceso a facturación enviado exitosamente')
    } catch (error) {
      console.error('Hubo un error: Error al enviar email de acceso a facturación ', error)
    }
  }

  // Reload history data
  await loadHistoryData()

  // Show success toast
  toastMessage.value = 'Éxito'
  toastSubmessage.value = 'Nuevo mes de facturación creado correctamente'
  toastType.value = 'success'
  toastIcon.value = checkIcon
  showToast.value = true
}

const handleModalSave = async (data: {
  paymentDueDate: string
  files: { file: File; fileType: FileTypes }[]
  billingMonth?: string
  invoiceNumber?: string
  amountToPay?: string
  concept?: string
  paymentMadeDate?: string
  sendByEmail?: boolean
  idEmpresa?: string | number | null
  idEmpresaFromGetGroups?: boolean
}) => {
  try {
    isLoading.value = true
    console.log('Modal save data:', data)

    if (isNewRegister.value) {
      await handleNewRegister(data)

      // If idEmpresa was obtained from getGroups, save it to the backend
      if (data.idEmpresaFromGetGroups && data.idEmpresa) {
        try {
          const businessGroupId = useBulkLoadStore().selectedGroup?.id
          if (businessGroupId) {
            await apiService.updateIdEmpresa(String(businessGroupId), String(data.idEmpresa))
          }
        } catch (error) {
          console.error('Hubo un error: Error al guardar idEmpresa ', error)
          // Don't throw here, as the main operation already succeeded
        }
      }
    }

    closeModal()
  } catch (error) {
    console.error('Hubo un error: Error saving document ', error)

    // Show error toast
    toastMessage.value = 'Error'
    toastSubmessage.value = 'Error al guardar el documento. Inténtalo de nuevo.'
    toastType.value = 'error'
    toastIcon.value = errorIcon
    showToast.value = true
  } finally {
    isLoading.value = false
  }
}

const downloadDocument = (filename: string) => {
  console.log('Download document event received:', filename)
  // The actual download logic is now handled in InvoiceCollectionsHistory component
}

const editDocument = (filename: string) => {
  console.log('Edit document event received:', filename)

  // Find the file in the history data to get the billing month and year
  const fileItem = historyData.value.find(
    (item) => item.accountStatement === filename || item.preInvoice === filename,
  )

  if (!fileItem) {
    console.error('Hubo un error: File not found in history data ', filename)
    return
  }

  // Determine document type
  const isAccountStatement = fileItem.accountStatement === filename
  editingDocumentType.value = isAccountStatement ? 'account-statement' : 'pre-invoice'

  // Extract month and year from billing month (format: "Septiembre 2025")
  const [month, year] = fileItem.billingMonth.split(' ')
  editingBillingMonth.value = month
  editingBillingYear.value = year
  editingPaymentDueDate.value = fileItem.paymentLimitDate
  editingFileName.value = filename
  editingPaymentMadeDate.value = fileItem.paymentMadeDate

  // Set as edit mode
  isNewRegister.value = false
  showModal.value = true
}

// Load history data when component mounts
onMounted(() => {
  loadHistoryData()
})
</script>

<style scoped>
.invoice-collections-container {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 0px;
  gap: 24px;
  width: 100%;
  min-height: 647px;
}

/* Button Section */
.button-section {
  display: flex;
  justify-content: flex-end;
  width: 100%;
  gap: 20px;
}

/* History styles moved to InvoiceCollectionsHistory component */
</style>
